// Proveedor de idioma: coordina i18next con la precedencia de idioma de la app.
//
// Precedencia (Req. i18n):
//   - SIN sesión: idioma recordado en localStorage o el del navegador (lo
//     resuelve la init de i18next). El selector del header lo cambia.
//   - CON sesión: el idioma GUARDADO del usuario en el backend PREVALECE. En
//     cuanto llega el perfil (`permissions.locale`), se aplica ese idioma.
//
// Expone `useLanguage()` con el idioma activo, los idiomas disponibles (según la
// cuenta, cuando hay sesión) y `setLocale`, que cambia el idioma Y lo persiste:
// en el backend si hay sesión (para que prevalezca en el próximo login) y
// siempre en localStorage como respaldo sin sesión.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.js';
import { useApiMaybe } from '../api/ApiContext.js';
import {
  SUPPORTED_CODE_LIST,
  SUPPORTED_LOCALES,
  isSupportedLocale,
  type Locale,
  type LocaleInfo,
} from './locales.js';
import { changeLocale, currentLocale } from './index.js';

interface LanguageState {
  /** Idioma activo. */
  locale: Locale;
  /** Idiomas disponibles para elegir (habilitados por la cuenta, si hay sesión). */
  available: LocaleInfo[];
  /** Cambia el idioma y lo persiste (backend si hay sesión + localStorage). */
  setLocale: (locale: Locale) => void;
}

const LanguageContext = createContext<LanguageState | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const { authenticated, permissions } = useAuth();
  const api = useApiMaybe();
  const [locale, setLocaleState] = useState<Locale>(() => currentLocale());

  // Mantiene el estado local en sintonía con cualquier cambio de i18next.
  useEffect(() => {
    const onChange = (lng: string) => {
      if (isSupportedLocale(lng)) setLocaleState(lng);
    };
    i18n.on('languageChanged', onChange);
    return () => {
      i18n.off('languageChanged', onChange);
    };
  }, [i18n]);

  // Al recibir el perfil del usuario, su idioma GUARDADO prevalece. Solo se
  // aplica una vez por sesión para no pisar un cambio manual posterior del
  // usuario dentro de la misma sesión.
  const appliedForSession = useRef(false);
  useEffect(() => {
    if (!authenticated) {
      appliedForSession.current = false;
      return;
    }
    if (appliedForSession.current || !permissions) return;
    appliedForSession.current = true;
    const saved = permissions.locale;
    if (isSupportedLocale(saved) && saved !== currentLocale()) {
      // Proviene del backend (fuente de verdad): no reescribe localStorage.
      void changeLocale(saved, false);
    }
  }, [authenticated, permissions]);

  // Idiomas disponibles: los habilitados por la cuenta cuando hay sesión; si no,
  // todos los soportados (home pública).
  const available = useMemo<LocaleInfo[]>(() => {
    const enabled = permissions?.enabledLocales;
    if (authenticated && Array.isArray(enabled) && enabled.length > 0) {
      const set = new Set(enabled);
      return SUPPORTED_LOCALES.filter((l) => set.has(l.code));
    }
    return [...SUPPORTED_LOCALES];
  }, [authenticated, permissions]);

  const setLocale = useCallback(
    (next: Locale) => {
      if (!isSupportedLocale(next)) return;
      void changeLocale(next, true);
      // Si hay sesión, persistir en el backend para que prevalezca en el login.
      if (authenticated && api) {
        void api.me.setLocale(next).catch(() => {
          // Best-effort: el idioma ya cambió en la UI; un fallo de red no revierte.
        });
      }
    },
    [authenticated, api],
  );

  const value = useMemo<LanguageState>(
    () => ({ locale, available, setLocale }),
    [locale, available, setLocale],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/** Hook del contexto de idioma. */
export function useLanguage(): LanguageState {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Respaldo defensivo si se usa fuera del provider: idioma activo de i18next,
    // todos los idiomas soportados, cambio solo local.
    return {
      locale: currentLocale(),
      available: [...SUPPORTED_LOCALES],
      setLocale: (next: Locale) => void changeLocale(next, true),
    };
  }
  return ctx;
}

export { SUPPORTED_CODE_LIST };
